import { useEffect, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { usePOS } from "@/context/POSContext";

/**
 * Order-type picker for the `order_types_enabled` CSV.
 *
 * The value is a comma list of order-type names, but an owner must never type
 * it: one typo silently 400s order creation for that type. This renders every
 * ACTIVE type from /api/order-types as a checkbox; ticking writes the CSV into
 * the page's draft like any other row — the save bar still owns the commit.
 *
 * The list is a protected route, so the session token goes with the request.
 * (It used to go without one, so the picker failed on every load and rendered
 * a sentence — "edit as text" — beside no text box at all.) If the list still
 * cannot be read, the raw text input takes over: the backend validator guards
 * the value either way, and a control that cannot work is not left dead (§45).
 */
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function OrderTypesPicker({ value, onChange, disabled }) {
  const { token } = usePOS();
  const [types, setTypes] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setFailed(false);
    axios
      .get(`${API}/order-types`, {
        params: { active_only: true },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then(({ data }) => { if (live) setTypes(Array.isArray(data) ? data : []); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [token]);

  const enabled = new Set((value || "").split(",").map(t => t.trim()).filter(Boolean));

  if (failed) {
    return (
      <div className="flex flex-col items-end gap-hair" data-testid="setting-order_types_enabled-fallback">
        <Input
          value={value ?? ""}
          onChange={event => onChange(event.target.value)}
          className="h-7 w-40 text-meta"
          disabled={disabled}
          placeholder="dine_in,takeaway"
          data-testid="setting-order_types_enabled-input"
        />
        <span className="text-micro text-muted-foreground">Could not load order types — comma-separated names; the server checks each one.</span>
      </div>
    );
  }
  if (!types) {
    return <span className="text-meta text-muted-foreground">Loading order types…</span>;
  }

  const toggle = name => {
    const next = new Set(enabled);
    if (next.has(name)) next.delete(name); else next.add(name);
    // Keep the table's order, not the tick order, so the CSV stays stable.
    onChange(types.map(t => t.name).filter(n => next.has(n)).join(","));
  };

  return (
    <div className="flex flex-col gap-snug" data-testid="setting-order_types_enabled-picker">
      {types.map(t => (
        <label key={t.id} className="flex flex-row items-center gap-2 cursor-pointer min-w-0">
          <input
            type="checkbox"
            className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
            checked={enabled.has(t.name)}
            onChange={() => toggle(t.name)}
            disabled={disabled}
            data-testid={`setting-order_types_enabled-${t.name}-checkbox`}
          />
          <span className="text-body select-none">{t.display_label || t.name}</span>
        </label>
      ))}
    </div>
  );
}
